import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Chart from '../../components/shared/LazyChart';
import {
    Cloud, Play, PauseCircle, DollarSign, ShieldCheck, UserX,
    MapPin, TrendingDown, Info, AlertTriangle, ServerCog,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getChartSeries, getChartNeutrals, isDarkTheme } from '../../lib/chartColors';
import { formatPercent } from '../../lib/formatNumber';
import { cn } from '../../lib/utils';
import cloudAssetService, {
    CloudAnalytics,
    CloudAnalyticsBreakdownRow,
    CloudAnalyticsTopResource,
} from '../../services/cloudAssetService';
import {
    aggregate, serverView, toggleSelection, isSelected,
    CloudDimension, CloudSelection, CloudDateRange, DIMENSION_LABELS,
} from './cloudAnalyticsModel';
import { FilterButton } from '../../components/shared/FilterButton';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import { CloudAnalyticsFilters } from './CloudAnalyticsFilters';
import {
    pct, fmt, KpiCard, SectionHead, EmptyStateCard, FocusableVisual,
    KPI_GRID, CONTENT_GRID, COL_PRIMARY, COL_SECONDARY, COL_HALF,
    CHART_HEIGHT, chartFont,
} from './analyticsPrimitives';

/** YYYY-MM → "Mar 2026", for the two time-series axes. */
const monthLabel = (month: string): string => {
    const [year, mon] = month.split('-').map(Number);
    if (!year || !mon) return month;
    return new Date(Date.UTC(year, mon - 1, 1)).toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });
};

const SEVERITY_LABELS: Record<string, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
};

/**
 * Analytics → Cloud (User Story 98).
 *
 * A read-only panel over `GET /cloud-insights/analytics`. It owns its own
 * fetch rather than joining the dashboard's `loadAll`, because none of the
 * dashboard's filters (location, category, asset date range) apply to a cloud
 * resource — pretending they did would show a filtered header above unfiltered
 * numbers. `refreshTick` is how the page's Refresh button reaches it, and
 * `onData` hands the payload back up for the page's Export CSV button.
 */
export const CloudAnalyticsPanel: React.FC<{
    refreshTick: number;
    onData?: (data: CloudAnalytics | null) => void;
    /**
     * Publishes this tab's Filters control so the page header can render it
     * beside Export CSV, where every other tab's Filters button already sits
     * (the others come from the page's own filter state; this tab's selection
     * lives down here, so the control has to be handed up). Null while the tab
     * cannot be filtered at all — no fact table, no cross-filtering.
     */
    onFilterControl?: (control: { activeCount: number; open: () => void } | null) => void;
}> = ({ refreshTick, onData, onFilterControl }) => {
    const { showToast } = useToast();
    // Amounts arrive in the register's base currency and are converted for
    // display here, the same as the Cloud module's own Cost tab.
    const { formatCost, formatCompact } = useCurrency();
    const CHART_COLORS = getChartSeries();
    const CHART_INK = getChartNeutrals();
    const CHART_TOOLTIP_THEME = isDarkTheme() ? 'dark' : 'light';

    const [data, setData] = useState<CloudAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    /* Cross-filter selection (Story 100). Keys per dimension; every visual is
       re-derived from the fact table against it. */
    const [selection, setSelection] = useState<CloudSelection>({});
    // The drawer edits a draft; charts change only on Apply, matching the
    // Assets/Licences drawers. Clicking a slice still filters immediately.
    const [filterDraft, setFilterDraft] = useState<CloudSelection>({});
    // Registration date window — a filter, not a chart dimension, so it is held
    // separately and narrows every visual including the one clicked.
    const [dateRange, setDateRange] = useState<CloudDateRange>({});
    const [dateDraft, setDateDraft] = useState<CloudDateRange>({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    // Held in a ref so an inline `onData` prop cannot re-trigger the fetch.
    const onDataRef = useRef(onData);
    onDataRef.current = onData;

    const load = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            const result = await cloudAssetService.getAnalytics(controller.signal);
            setData(result);
            // A refresh can retire the very resources a selection referred to;
            // keeping stale keys would silently filter everything away.
            setSelection({});
            setFilterDraft({});
            setDateRange({});
            setDateDraft({});
            setError(null);
        } catch (err: any) {
            // A superseded/unmounted request is not a failure the user caused.
            if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') return;
            const message = err?.friendlyMessage || 'Failed to load cloud analytics';
            setError(message);
            showToast(message, 'error');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        load();
        return () => abortRef.current?.abort();
    }, [load, refreshTick]);

    /* ── cross-filter selection ── */
    // Selection is only possible when the fact table came through; above the
    // row cap the tab still renders, from the server's own aggregates.
    const canCrossFilter = !!data && !data.factsTruncated && data.facts.length > 0;
    const view = useMemo(
        () => (data ? (canCrossFilter ? aggregate(data, selection, dateRange) : serverView(data)) : null),
        [data, selection, dateRange, canCrossFilter],
    );
    const activeSelections = useMemo(
        () => (Object.entries(selection) as [CloudDimension, string[]][])
            .flatMap(([dimension, keys]) => keys.map((key) => ({
                dimension,
                key,
                label: data?.facts.find((f) => String(f[dimension]) === key)
                    ? String(data.facts.find((f) => String(f[dimension]) === key)![
                        dimension === 'lifecycle' ? 'lifecycle' : (`${dimension}Label` as keyof typeof data.facts[0])
                    ])
                    : key,
            }))),
        [selection, data],
    );

    // Hand the *derived* view up, not the raw response: the page's Export CSV
    // must emit what is on screen, selection included.
    useEffect(() => {
        onDataRef.current?.(data && view ? { ...data, ...view } : null);
    }, [data, view]);

    const filterChips: FilterChip[] = useMemo(() => {
        const chips: FilterChip[] = activeSelections.map(({ dimension, key, label }) => ({
            key: `${dimension}:${key}`,
            label: `${DIMENSION_LABELS[dimension]}: ${label}`,
            onRemove: () => setSelection((current) => toggleSelection(current, dimension, key, true)),
        }));
        if (dateRange.startDate || dateRange.endDate) {
            chips.push({
                key: 'registered',
                label: `Registered: ${dateRange.startDate || '…'} → ${dateRange.endDate || '…'}`,
                onRemove: () => { setDateRange({}); setDateDraft({}); },
            });
        }
        return chips;
    }, [activeSelections, dateRange]);

    /* Publish the Filters control to the page header. Opening the drawer has to
       seed the draft from the live selection, so the handler is rebuilt when
       either moves; held in a ref on the parent's side so this cannot loop. */
    const openFilterDrawer = useCallback(() => {
        setFilterDraft(selection);
        setDateDraft(dateRange);
        setFilterDrawerOpen(true);
    }, [selection, dateRange]);

    const onFilterControlRef = useRef(onFilterControl);
    onFilterControlRef.current = onFilterControl;
    useEffect(() => {
        onFilterControlRef.current?.(
            canCrossFilter ? { activeCount: filterChips.length, open: openFilterDrawer } : null,
        );
        // Withdraw the control when the tab unmounts, or the header would keep
        // a Filters button wired to a panel that is no longer on screen.
        return () => onFilterControlRef.current?.(null);
    }, [canCrossFilter, filterChips.length, openFilterDrawer]);

    const select = useCallback((dimension: CloudDimension, key: string, additive: boolean) => {
        setSelection((current) => toggleSelection(current, dimension, key, additive));
    }, []);

    /** Unselected slices fade so the selected one reads as the active filter. */
    const seriesColors = useCallback((rows: CloudAnalyticsBreakdownRow[], dimension?: CloudDimension) => {
        const palette = rows.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
        if (!dimension || !(selection[dimension]?.length)) return palette;
        return palette.map((color, i) => (isSelected(selection, dimension, rows[i].key) ? color : `${color}33`));
    }, [CHART_COLORS, selection]);

    /**
     * ApexCharts hands back the clicked index; rows are in the same order.
     *
     * Wired only when cross-filtering is actually possible. Without the guard a
     * click above the row cap would set a selection nothing can apply, showing
     * a "Filtered by" chip over completely unfiltered numbers.
     */
    const pointSelection = useCallback((rows: CloudAnalyticsBreakdownRow[], dimension?: CloudDimension) => (
        dimension && canCrossFilter ? {
            dataPointSelection: (event: any, _ctx: any, config: any) => {
                const row = rows[config?.dataPointIndex];
                if (!row) return;
                const additive = !!(event?.ctrlKey || event?.metaKey || event?.shiftKey);
                // ApexCharts keeps working on its own SVG after this callback
                // returns — a donut goes on to redraw its inner labels. Updating
                // state synchronously re-renders the chart, detaching the nodes
                // it is about to query, and it throws on a null querySelector.
                // Handing the update to the next tick lets it finish first.
                setTimeout(() => select(dimension, row.key, additive), 0);
            },
        } : {}
    ), [select, canCrossFilter]);

    /* ── chart option builders ── */
    const donutOptions = useCallback((
        rows: CloudAnalyticsBreakdownRow[], id: string, dimension?: CloudDimension,
    ): ApexCharts.ApexOptions => ({
        chart: {
            id, type: 'donut', ...chartFont,
            events: pointSelection(rows, dimension),
        },
        labels: rows.map((r) => r.label),
        colors: seriesColors(rows, dimension),
        legend: { position: 'bottom', fontSize: '12px', labels: { colors: CHART_INK.axisLabel } },
        dataLabels: { enabled: true, formatter: (v: number) => formatPercent(Number(v)) },
        stroke: { width: 0 },
        states: { active: { filter: { type: 'none' } } },
        plotOptions: { pie: { expandOnClick: false } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    }), [CHART_INK, CHART_TOOLTIP_THEME, pointSelection, seriesColors]);

    const barOptions = useCallback((
        rows: CloudAnalyticsBreakdownRow[], id: string, money = false, dimension?: CloudDimension,
    ): ApexCharts.ApexOptions => ({
        chart: {
            id, type: 'bar', ...chartFont, toolbar: { show: false },
            events: pointSelection(rows, dimension),
        },
        colors: seriesColors(rows, dimension),
        plotOptions: { bar: { borderRadius: 6, borderRadiusApplication: 'end', horizontal: true, barHeight: '55%', distributed: true } },
        dataLabels: {
            enabled: true, offsetX: 26,
            style: { fontSize: '11px', fontWeight: 700, colors: [CHART_INK.labelStrong] },
            formatter: (v: number) => (money ? formatCompact(Number(v)) : fmt(Number(v))),
        },
        xaxis: {
            categories: rows.map((r) => r.label),
            labels: {
                style: { fontSize: '11px', colors: CHART_INK.axisLabel },
                formatter: (v: string) => (money ? formatCompact(Number(v)) : String(v)),
            },
        },
        yaxis: { labels: { style: { fontSize: '12px', colors: CHART_INK.axisLabel } } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4, yaxis: { lines: { show: false } } },
        legend: { show: false },
        states: { active: { filter: { type: 'none' } } },
        tooltip: {
            theme: CHART_TOOLTIP_THEME,
            y: { formatter: (v: number) => (money ? formatCost(Number(v)) : fmt(Number(v))) },
        },
    }), [CHART_INK, CHART_TOOLTIP_THEME, formatCompact, formatCost, pointSelection, seriesColors]);

    /** Bars grow with the category count so long lists stay readable. */
    const barHeight = (rows: unknown[]) => Math.max(CHART_HEIGHT, rows.length * 40);

    /** Says a chart is clickable, since nothing else about it suggests that. */
    const selectableNote = (note: string | undefined, dimension?: CloudDimension) => {
        if (!dimension || !canCrossFilter) return note;
        const hint = 'Click to filter · ctrl-click to add';
        return note ? `${note} · ${hint}` : hint;
    };

    const kpis = view?.kpis;

    const createdSeries = useMemo(() => view?.createdOverTime ?? [], [view]);
    const costSeries = useMemo(() => view?.costTrend ?? [], [view]);

    const growthOptions: ApexCharts.ApexOptions = {
        chart: { id: 'cloud-analytics-growth', type: 'line', ...chartFont, toolbar: { show: false } },
        colors: [CHART_COLORS[0], CHART_COLORS[2]],
        stroke: { width: [0, 3], curve: 'smooth' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: createdSeries.map((p) => monthLabel(p.month)), labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        yaxis: [
            { title: { text: 'Added', style: { color: CHART_INK.axisLabel } }, labels: { style: { colors: CHART_INK.axisLabel } } },
            { opposite: true, title: { text: 'Total registered', style: { color: CHART_INK.axisLabel } }, labels: { style: { colors: CHART_INK.axisLabel } } },
        ],
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4 },
        legend: { position: 'top', horizontalAlign: 'right', labels: { colors: CHART_INK.axisLabel } },
        tooltip: { theme: CHART_TOOLTIP_THEME },
    };

    // Story 109 — was `type: 'area'` with a gradient fill, which visually implies
    // a running total. Each point is that month's own spend, not a cumulative
    // sum, so this is a plain line like every other trend chart in the app.
    const costTrendOptions: ApexCharts.ApexOptions = {
        chart: { id: 'cloud-analytics-cost-trend', type: 'line', ...chartFont, toolbar: { show: false } },
        colors: [CHART_COLORS[1]],
        stroke: { width: 3, curve: 'smooth' },
        markers: { size: 4 },
        dataLabels: { enabled: false },
        xaxis: { categories: costSeries.map((p) => monthLabel(p.month)), labels: { style: { fontSize: '11px', colors: CHART_INK.axisLabel } } },
        yaxis: { labels: { style: { colors: CHART_INK.axisLabel }, formatter: (v: number) => formatCompact(Number(v)) } },
        grid: { borderColor: CHART_INK.gridLine, strokeDashArray: 4 },
        legend: { show: false },
        tooltip: { theme: CHART_TOOLTIP_THEME, y: { formatter: (v: number) => formatCost(Number(v)) } },
    };

    /* ── reusable section shells ──
       Each one is a FocusableVisual, so every chart on this tab can be opened
       on its own at viewport size. In focus mode a bar chart drops its inline
       scroll cap and takes the height it is given. */
    const CountChart: React.FC<{
        title: string; note?: string; rows: CloudAnalyticsBreakdownRow[];
        id: string; type: 'bar' | 'donut'; emptyReason?: string; className?: string;
        /** Dimension this chart selects on. Omit to make it read-only. */
        dimension?: CloudDimension;
    }> = ({ title, note, rows, id, type, emptyReason, className, dimension }) => (
        <FocusableVisual title={title} note={selectableNote(note, dimension)} className={className}>
            {({ height, focused }) => (
                rows.length === 0 ? (
                    <EmptyStateCard title="Nothing recorded yet" reason={emptyReason} />
                ) : type === 'donut' ? (
                    <Chart
                        options={donutOptions(rows, id, dimension)}
                        series={rows.map((r) => r.count)}
                        type="donut" height={height}
                    />
                ) : (
                    <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                        <Chart
                            options={barOptions(rows, id, false, dimension)}
                            series={[{ name: 'Resources', data: rows.map((r) => r.count) }]}
                            type="bar" height={focused ? Math.max(height, rows.length * 34) : barHeight(rows)}
                        />
                    </div>
                )
            )}
        </FocusableVisual>
    );

    const CostChart: React.FC<{
        title: string; note?: string; rows: CloudAnalyticsBreakdownRow[];
        id: string; className?: string; dimension?: CloudDimension;
    }> = ({ title, note, rows, id, className, dimension }) => {
        const withCost = rows.filter((r) => r.monthlyCost > 0);
        return (
            <FocusableVisual title={title} note={selectableNote(note, dimension)} className={className}>
                {({ height, focused }) => (
                    withCost.length === 0 ? (
                        <EmptyStateCard title="No spend recorded" reason="Monthly cost is entered by hand on each resource; none of these carry a figure yet." />
                    ) : (
                        <div className={cn(!focused && 'max-h-[420px] overflow-y-auto')}>
                            <Chart
                                options={barOptions(withCost, id, true, dimension)}
                                series={[{ name: 'Monthly cost', data: withCost.map((r) => Math.round(r.monthlyCost * 100) / 100) }]}
                                type="bar" height={focused ? Math.max(height, withCost.length * 34) : barHeight(withCost)}
                            />
                        </div>
                    )
                )}
            </FocusableVisual>
        );
    };

    const ResourceTable: React.FC<{ rows: CloudAnalyticsTopResource[]; emptyTitle: string; emptyReason?: string }> = ({ rows, emptyTitle, emptyReason }) => (
        rows.length === 0
            ? <EmptyStateCard title={emptyTitle} reason={emptyReason} />
            : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Resource</TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead>Environment</TableHead>
                            <TableHead>State</TableHead>
                            <TableHead className="text-right">Monthly cost</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.id}>
                                <TableCell>
                                    <span className="font-medium text-foreground">{r.name}</span>
                                    <span className="block text-xs text-muted-foreground">
                                        {[r.resourceType, r.department].filter(Boolean).join(' · ') || '—'}
                                    </span>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{r.provider}</TableCell>
                                <TableCell className="text-muted-foreground">
                                    {[r.environment, r.environmentTier].filter(Boolean).join(' · ') || '—'}
                                </TableCell>
                                <TableCell className="capitalize text-muted-foreground">{r.providerState}</TableCell>
                                <TableCell className="text-right tabular-nums font-semibold text-foreground">
                                    {formatCost(r.monthlyCostBase)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )
    );

    /* ── states ── */
    if (loading && !data) {
        return (
            <div className="analytics-panel">
                <Card><EmptyStateCard title="Loading cloud analytics…" spinning /></Card>
            </div>
        );
    }
    if (error && !data) {
        return (
            <div className="analytics-panel">
                <Card>
                    <EmptyStateCard
                        icon={<AlertTriangle size={20} />}
                        title="Cloud analytics unavailable"
                        reason={error}
                        action={{ label: 'Retry', onClick: () => load() }}
                    />
                </Card>
            </div>
        );
    }
    if (!data || !view || !kpis) return null;

    /* ── Applied filters ──
       The Filters *button* now lives in the page header beside Export CSV
       (published through `onFilterControl`), so what stays here is what the
       header cannot carry: the chips and how much of the estate survived them.

       It renders only while something is applied — unfiltered, the row said
       "21 resources", which the Total Cloud Resources KPI directly below
       already says. Still declared here rather than inline because it must also
       render above the "nothing matched" state: a filter that empties the tab
       and takes its own Clear control with it is a trap only a reload escapes. */
    const filterSection = !canCrossFilter || !data || !view || filterChips.length === 0 ? null : (
                <div className="rounded-md border bg-card">
                    <div className="flex items-center justify-end gap-3 px-4 pt-3">
                        <span className="text-xs text-muted-foreground">
                            {fmt(kpis.totalResources)} of {fmt(data.kpis.totalResources)} resources
                        </span>
                    </div>
                    <FilterChipsBar
                        chips={filterChips}
                        onClearAll={() => { setSelection({}); setDateRange({}); setDateDraft({}); }}
                    />
                </div>
    );

    if (kpis.totalResources === 0) {
        // An empty register and an over-narrow filter look identical on screen
        // but need opposite advice, so they are separate states.
        const filtered = data.kpis.totalResources > 0;
        return (
            <div className="analytics-panel">
                {filtered && filterSection}
                <Card>
                    <EmptyStateCard
                        icon={<Cloud size={20} />}
                        title={filtered ? 'No resources match these filters' : 'No cloud resources in your scope'}
                        reason={filtered
                            ? `None of the ${fmt(data.kpis.totalResources)} resources in your scope match every filter applied above.`
                            : 'Cloud analytics is built from the cloud register. Once resources are added or imported in Cloud Assets, every chart on this tab fills in.'}
                        action={filtered
                            ? {
                                label: 'Clear filters',
                                onClick: () => {
                                    setSelection({}); setFilterDraft({});
                                    setDateRange({}); setDateDraft({});
                                },
                            }
                            : undefined}
                    />
                </Card>
                {filtered && (
                    <CloudAnalyticsFilters
                        open={filterDrawerOpen}
                        onOpenChange={setFilterDrawerOpen}
                        facts={data.facts}
                        draft={filterDraft}
                        onDraftChange={setFilterDraft}
                        dateDraft={dateDraft}
                        onDateDraftChange={setDateDraft}
                        onApply={() => { setSelection(filterDraft); setDateRange(dateDraft); }}
                        onClear={() => {
                            setSelection({}); setFilterDraft({});
                            setDateRange({}); setDateDraft({});
                        }}
                    />
                )}
            </div>
        );
    }

    // Two-slice donuts with no dimension behind them: readable, not selectable.
    const complianceSeries = [kpis.compliantResources, kpis.nonCompliantResources];
    const complianceRows = ['Compliant', 'Has findings'].map((label, i) => ({
        key: label, label, count: complianceSeries[i], monthlyCost: 0,
    }));
    const activeSeries = [kpis.activeResources, kpis.inactiveResources];
    const activeRows = ['Active', 'Inactive'].map((label, i) => ({
        key: label, label, count: activeSeries[i], monthlyCost: 0,
    }));

    return (
        <div className={cn('analytics-panel', loading && 'opacity-60 transition-opacity')}>
            {/* Manual-entry caveat, stated once at the top rather than on every card. */}
            <div className="analytics-partial-banner">
                <Info size={14} />
                <span>
                    Every figure below is typed into the cloud register by hand — there are no
                    provider connections{kpis.costAsOf ? `. Oldest cost figure on record: ${new Date(kpis.costAsOf).toLocaleDateString()}` : ''}
                    {kpis.staleCostCount > 0 ? ` · ${fmt(kpis.staleCostCount)} cost figure(s) not updated in 90+ days` : ''}
                    {kpis.unconvertibleCurrencies.length > 0 ? ` · excluded from spend, no exchange rate: ${kpis.unconvertibleCurrencies.join(', ')}` : ''}
                </span>
            </div>

            {filterSection}

            {/* ── KPI strip ── */}
            <div className={KPI_GRID}>
                <KpiCard icon={<Cloud size={18} />} label="Total Cloud Resources" value={kpis.totalResources} sub={`${fmt(kpis.distinctProviders)} provider(s) · ${fmt(kpis.distinctRegions)} region(s)`} accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" />
                <KpiCard icon={<Play size={18} />} label="Running" value={kpis.running} sub={`${formatPercent(pct(kpis.running, kpis.totalResources))} of the estate`} accent="#059669" iconBg="rgba(13,148,136,0.12)" />
                <KpiCard icon={<PauseCircle size={18} />} label="Stopped / Terminated" value={kpis.stopped + kpis.terminated} sub={`${fmt(kpis.unknownState)} in an unknown state`} accent="#d97706" iconBg="rgba(217,119,6,0.10)" />
                <KpiCard icon={<DollarSign size={18} />} label="Monthly Cloud Spend" value={formatCost(kpis.monthlyCost)} sub={kpis.resourcesWithoutCost > 0 ? `Understated — ${fmt(kpis.resourcesWithoutCost)} resource(s) carry no cost` : 'Every resource carries a cost figure'} accent="#7c3aed" iconBg="rgba(124,58,237,0.10)" />
                <KpiCard icon={<TrendingDown size={18} />} label="Idle Candidates" value={kpis.idleCandidates} sub={`${formatCost(kpis.idleMonthlyCost)}/mo · off at the provider, still Active here`} accent="#e11d48" iconBg="rgba(225,29,72,0.10)" />
                <KpiCard icon={<ShieldCheck size={18} />} label="Register Compliance" value={formatPercent(kpis.compliancePercent)} sub={`${fmt(kpis.nonCompliantResources)} resource(s) with audit findings`} accent="#0891b2" iconBg="#ecfeff" />
                <KpiCard icon={<UserX size={18} />} label="Unowned Resources" value={kpis.unownedResources} sub="No business or technical owner" accent="#e11d48" iconBg="rgba(225,29,72,0.10)" />
                <KpiCard icon={<MapPin size={18} />} label="Active Resources" value={kpis.activeResources} sub={`${fmt(kpis.inactiveResources)} inactive by lifecycle status`} accent="#4f46e5" iconBg="rgba(79,70,229,0.10)" />
            </div>

            {/* ── Growth and spend ──
                Deliberately ahead of the composition breakdowns: how the estate
                is changing and what it costs are the questions this tab gets
                opened for, and the "resources by X" cuts are what you reach for
                once one of those numbers moves. */}
            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_PRIMARY} title="Resources Added Over Time" note="By date registered here — providers report no creation date to this system">
                    {({ height }) => (
                        createdSeries.length === 0 ? (
                            <EmptyStateCard title="No registration history" />
                        ) : (
                            <Chart
                                options={growthOptions}
                                series={[
                                    { name: 'Added', type: 'column', data: createdSeries.map((p) => p.count) },
                                    { name: 'Total registered', type: 'line', data: createdSeries.map((p) => p.cumulative) },
                                ]}
                                type="line" height={height}
                            />
                        )
                    )}
                </FocusableVisual>
                <FocusableVisual className={COL_SECONDARY} title="Lifecycle Status" note="Where a planned decommission is recorded">
                    {({ height }) => (
                        view.byLifecycleStatus.length === 0
                            ? <EmptyStateCard title="No lifecycle data" />
                            : <Chart
                                options={donutOptions(view.byLifecycleStatus, 'cloud-analytics-lifecycle', 'lifecycle')}
                                series={view.byLifecycleStatus.map((r) => r.count)}
                                type="donut" height={height}
                            />
                    )}
                </FocusableVisual>
            </div>

            <div className={CONTENT_GRID}>
                <CostChart className={COL_HALF} title="Cloud Cost by Provider" rows={view.byProvider} id="cloud-analytics-cost-provider" dimension="provider" />
                <CostChart className={COL_HALF} title="Cloud Cost by Environment" note="Prod / staging / dev tier" rows={view.byEnvironmentTier} id="cloud-analytics-cost-tier" dimension="tier" />
            </div>

            <FocusableVisual
                title="Monthly Cloud Cost Trend"
                note={data.costTrendReconstructed
                    ? 'Rebuilt from recorded cost edits — a month nobody edited carries the figure in force at the time'
                    : 'No cost edits recorded yet, so every month shows today’s figures'}
            >
                {({ height }) => (
                    costSeries.length === 0
                        ? <EmptyStateCard title="No spend history" />
                        : <Chart
                            options={costTrendOptions}
                            series={[{ name: 'Monthly cost', data: costSeries.map((p) => Math.round(p.monthlyCost * 100) / 100) }]}
                            type="line" height={height}
                        />
                )}
            </FocusableVisual>

            {/* ── Estate composition ── */}
            <div className={CONTENT_GRID}>
                <CountChart className={COL_HALF} title="Resources by Cloud Provider" rows={view.byProvider} id="cloud-analytics-provider" type="donut" dimension="provider" />
                <CountChart className={COL_HALF} title="Resources by Environment" note="Prod / staging / dev tier" rows={view.byEnvironmentTier} id="cloud-analytics-tier" type="donut" dimension="tier" />
            </div>

            <div className={CONTENT_GRID}>
                <CountChart className={COL_PRIMARY} title="Resources by Type" rows={view.byResourceType} id="cloud-analytics-type" type="bar" emptyReason="No resource has been mapped to a normalized type yet." dimension="resourceType" />
                <CountChart className={COL_SECONDARY} title="Resource State" note="As recorded at the provider" rows={view.byProviderState} id="cloud-analytics-state" type="donut" dimension="state" />
            </div>

            <div className={CONTENT_GRID}>
                <CountChart className={COL_PRIMARY} title="Resources by Region" rows={view.byRegion} id="cloud-analytics-region" type="bar" emptyReason="No region has been recorded on these resources." dimension="region" />
                <FocusableVisual className={COL_SECONDARY} title="Active vs Inactive" note="ITAM lifecycle status">
                    {({ height }) => (
                        <Chart options={donutOptions(activeRows, 'cloud-analytics-active')} series={activeSeries} type="donut" height={height} />
                    )}
                </FocusableVisual>
            </div>

            <div className={CONTENT_GRID}>
                <CountChart className={COL_HALF} title="Resources by Business Unit" note="Owning department" rows={view.byDepartment} id="cloud-analytics-department" type="bar" dimension="department" />
                <CountChart className={COL_HALF} title="Resources by Owner" note="Business owner, or technical owner where none is set" rows={view.byOwner} id="cloud-analytics-owner" type="bar" dimension="owner" />
            </div>

            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_PRIMARY} title="Top Costly Resources" note="Highest monthly spend in your scope" flush>
                    {() => (
                        <ResourceTable rows={view.topCostlyResources} emptyTitle="No costed resources" emptyReason="No resource in scope carries a monthly cost figure yet." />
                    )}
                </FocusableVisual>
                <FocusableVisual className={COL_SECONDARY} title="Compliance Status" note="Against the cloud data-completeness audit">
                    {({ height }) => (
                        <Chart options={donutOptions(complianceRows, 'cloud-analytics-compliance')} series={complianceSeries} type="donut" height={height} />
                    )}
                </FocusableVisual>
            </div>

            <div className={CONTENT_GRID}>
                <FocusableVisual className={COL_SECONDARY} title="Governance Risk by Severity" note="Data-completeness findings, not security scan results" flush>
                    {() => (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Severity</TableHead>
                                    <TableHead>Findings</TableHead>
                                    <TableHead>Resources</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {view.riskBySeverity.map((row) => (
                                    <TableRow key={row.severity}>
                                        <TableCell>
                                            <span className={`risk-dot risk-${row.severity === 'high' ? 'critical' : row.severity === 'medium' ? 'warning' : 'info'}`} />
                                            {SEVERITY_LABELS[row.severity] ?? row.severity}
                                        </TableCell>
                                        <TableCell className="tabular-nums font-semibold text-foreground">{fmt(row.findingCount)}</TableCell>
                                        <TableCell className="tabular-nums text-muted-foreground">{fmt(row.resourceCount)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </FocusableVisual>
                <FocusableVisual
                    className={COL_PRIMARY}
                    title="Idle / Underutilized Candidates"
                    note="Stopped or terminated at the provider, still Active in the register"
                    flush
                >
                    {() => (
                        <ResourceTable
                            rows={view.idleResources}
                            emptyTitle="No idle candidates"
                            emptyReason="Nothing in scope is stopped or terminated while still marked Active."
                        />
                    )}
                </FocusableVisual>
            </div>

            <CloudAnalyticsFilters
                open={filterDrawerOpen}
                onOpenChange={setFilterDrawerOpen}
                facts={data.facts}
                draft={filterDraft}
                onDraftChange={setFilterDraft}
                dateDraft={dateDraft}
                onDateDraftChange={setDateDraft}
                onApply={() => { setSelection(filterDraft); setDateRange(dateDraft); }}
                onClear={() => {
                    setSelection({}); setFilterDraft({});
                    setDateRange({}); setDateDraft({});
                }}
            />

            {/* ── Honest gaps ──
                Named with their cause rather than dropped, so a missing chart
                reads as a data-model limit instead of an unfinished screen. */}
            {data.unavailableMetrics.length > 0 && (
                <Card>
                    <CardContent className="p-5">
                        <SectionHead title="Not available from this data" note="Charts that need fields this register does not hold" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            {data.unavailableMetrics.map((metric) => (
                                <div key={metric.id} className="flex gap-2.5 rounded-md border border-dashed border-border p-3">
                                    <ServerCog size={16} className="mt-0.5 shrink-0 text-muted-foreground/60" />
                                    <div>
                                        <p className="text-sm font-medium text-foreground">{metric.label}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">{metric.reason}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default CloudAnalyticsPanel;
